import { useState } from "react";
import { apiFetchOrThrow } from "../api/client";
import type { AuthUser } from "../api/auth";
import { AnalyticsEvent, track } from "../lib/analytics";
import { useOnlineStatus } from "../lib/online";
import { useAuthStore } from "../store/auth";

export type NewsletterState = NonNullable<AuthUser["newsletter"]>;

/** Stamps each toggle, so only the latest PUT's answer lands. */
let newsletterRequest = 0;

function setNewsletter(newsletter: NewsletterState) {
  useAuthStore.setState((s) => (s.user ? { user: { ...s.user, newsletter } } : s));
}

/**
 * The account's newsletter toggle, shared by Settings and the What's New page.
 * Its state comes with the account (`GET /auth/me`) and is kept on the
 * persisted user, so it shows the right value at once and offline; callers
 * disable it offline. A flip moves the switch and the stored state at once, and
 * the PUT runs in the background; it springs back only if the server refuses.
 */
export function useNewsletterToggle() {
  const newsletter = useAuthStore((s) => s.user?.newsletter);
  const online = useOnlineStatus();
  const [failed, setFailed] = useState(false);

  const toggle = (subscribed: boolean) => {
    const request = ++newsletterRequest;
    setNewsletter({ subscribed, available: true });
    setFailed(false);
    apiFetchOrThrow<NewsletterState>("/newsletter/me", {
      method: "PUT",
      body: JSON.stringify({ subscribed }),
    })
      .then((state) => {
        if (request === newsletterRequest) setNewsletter(state);
        track(AnalyticsEvent.SettingChanged, { setting: "newsletter", value: subscribed });
      })
      .catch(() => {
        if (request !== newsletterRequest) return;
        setNewsletter({ subscribed: !subscribed, available: true });
        setFailed(true);
      });
  };

  return { newsletter, online, failed, toggle };
}
